import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useScenarioMode } from "../../context/ScenarioModeContext";
import { ScenarioBanner, ScenarioToggle } from "../../components/planning/ScenarioModeControls";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import PnlSummary from "../../components/planning/pnl/PnlSummary";
import PnlGrid from "../../components/planning/pnl/PnlGrid";
import PnlDrilldownPanel from "../../components/planning/pnl/PnlDrilldownPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function PnlPage() {
  const { scenarioMode } = useScenarioMode();
  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drilldown, setDrilldown] = useState(null); // { line, month }

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api
      .get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  useEffect(() => {
    if (!companyId || !year) return;
    setLoading(true);
    api
      .get(scenarioMode ? "/scenario-outcome/pnl" : "/pnl", { params: { company_id: companyId, year } })
      .then((res) => setData(res.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [companyId, year, scenarioMode]);

  return (
    <div data-testid="pnl-page">
      <PageHeader
        title="Profit & Loss"
        subtitle="Read-only company result — actual + current forecast = expected outcome. Correct numbers in their source module."
        actions={
          <div className="flex items-center gap-2">
            <ScenarioToggle />
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="pnl-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="pnl-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`pnl-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />
      <ScenarioBanner meta={data?.scenario_meta} />

      {companyId && years.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="pnl-no-years">
          <EmptyState
            title="No financial years created"
            description="Create a financial year under Administration → Companies before viewing the P&L."
            testId="pnl-no-years-state"
          />
        </div>
      )}

      {year && data && <PnlSummary fy={data.fy} ytd={data.ytd} targets={data.targets} />}

      {year && (
        <PnlGrid
          data={data}
          loading={loading}
          onDrilldown={(line, month) => setDrilldown({ line, month })}
        />
      )}

      <PnlDrilldownPanel
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        line={drilldown?.line}
        month={drilldown?.month || 1}
        data={data}
        year={year}
      />
    </div>
  );
}
